export enum OverscrollDirection {
    top = 'top',
    bottom = 'bottom',
}

export declare type OverscrollOptions = {
    direction: OverscrollDirection
    rootMargin: string
    // IntersectionObserver root. Defaults to the observed element. Pass `null` to
    // observe against the viewport when the page itself scrolls (e.g. the timeline).
    root?: HTMLElement | null
}

export const useInfiniteScroll = (
    el: HTMLElement | undefined,
    ender: HTMLElement | undefined,
    callback: Function,
    options: OverscrollOptions = { direction: OverscrollDirection.bottom, rootMargin: '10px' }
) => {
    if (!el || !ender) {
        return
    }

    onOverscroll(el, callback, options)

    // When `el` is not the scrolling element (e.g. the homepage timeline scrolls
    // the window), using it as the observer root leaves the ender permanently
    // "intersecting", so the observer never re-fires and infinite scroll stalls
    // after the first page. Such callers pass `root: null` to observe the viewport.
    const observerRoot = options.root !== undefined ? options.root : el

    const observer = new IntersectionObserver(
        (entries) => {
            let entry = entries[0]

            if (entry.isIntersecting) {
                callback()
            }
        },
        { root: observerRoot, rootMargin: options.rootMargin }
    )

    if (ender) {
        observer.observe(ender)
    }
}

export const onOverscroll = (
    el: HTMLElement,
    callback: Function,
    options: OverscrollOptions = { direction: OverscrollDirection.bottom, rootMargin: '10px' }
) => {
    el.addEventListener('scroll', () => {
        if (!hasBeenScrolledToEnd(el)) {
            return
        }

        callback()
    })

    // Check for desktop mousewheel overscroll
    onWheelEnd(el, (e: WheelEvent) => {
        if (!hasBeenScrolledToEnd(el)) {
            return
        }

        if ((options.direction === OverscrollDirection.bottom && e.deltaY > 0) || (options.direction === OverscrollDirection.top && e.deltaY < 0)) {
            callback()
        }
    })

    // Check for mobile overscroll
    el.addEventListener('touchend', function () {
        if (hasBeenScrolledToEnd(el)) {
            callback()
        }
    })
}

export const onWheelEnd = (el: HTMLElement, callback: Function) => {
    el.addEventListener('wheel', wheel, { passive: true })

    let marker: boolean = true,
        delta: number,
        direction: string = 'down',
        interval: number = 30,
        counter1: number = 0,
        counter2: number

    function wheel(e: WheelEvent): boolean {
        counter1 += 1
        delta = e.deltaY || e.detail
        if (Math.abs(delta) < 50) {
            return false
        }

        direction = delta > 0 ? 'up' : 'down'

        if (marker) {
            wheelStart(e)
        }

        return false
    }

    function wheelStart(e: WheelEvent): void {
        marker = false
        wheelAct(e)
    }

    function wheelAct(e: WheelEvent): void {
        counter2 = counter1
        setTimeout(function () {
            if (counter2 == counter1 || Math.abs(delta) < 50) {
                wheelEnd(e)
            } else {
                wheelAct(e)
            }
        }, interval)
    }

    function wheelEnd(e: WheelEvent): void {
        marker = true
        counter1 = 0
        counter2 = 0

        callback(e)
    }
}

export const hasBeenScrolledToEnd = (el: HTMLElement) => {
    const scrollableHeight = Math.round(el.scrollHeight - el.clientHeight)

    return Math.round(el.scrollTop) >= scrollableHeight
}
